/**
 * Safe Cart Progress Bar Manager - No interference with cart operations
 * progress-bar-dynamic.js
 */
class CartProgressBarManager {
  constructor() {
    this.containers = document.querySelectorAll('.progress-bar-section');
    this.lastCartTotal = null;
    this.lastItemCount = null;
    this.isUpdating = false;
    
    if (this.containers.length === 0) return;
    
    this.initializeProgressBar();
  }

  initializeProgressBar() {
    this.loadTierSettings();
    this.setupSafeEventListeners(); // Changed to safe listeners
    this.startSafeMonitoring(); // Changed to safe monitoring
    this.forceInitialUpdate();
  }

  async forceInitialUpdate() {
    setTimeout(() => this.updateAllProgressBars(), 100);
    setTimeout(() => this.updateAllProgressBars(), 500);
    setTimeout(() => this.updateAllProgressBars(), 1000);
    this.updateAllProgressBars();
  }

  loadTierSettings() {
    const firstContainer = this.containers[0];
    if (!firstContainer) return;
    
    const dataset = firstContainer.dataset;
    
    this.activeTiers = [
      { 
        price: parseFloat(dataset.price1) || 0, 
        heading: dataset.heading1 || 'Free Shipping', 
        level: 1 
      },
      { 
        price: parseFloat(dataset.price2) || 0, 
        heading: dataset.heading2 || `${dataset.discount2 || '10'}% Discount`, 
        level: 2 
      },
      { 
        price: parseFloat(dataset.price3) || 0, 
        heading: dataset.heading3 || `${dataset.discount3 || '50'}% Discount`, 
        level: 3 
      }
    ]
    .filter(tier => tier.price > 0)
    .sort((a, b) => a.price - b.price)
    .map(tier => ({ ...tier, priceInPaise: tier.price * 100 }));
    
    console.log('Active tiers loaded:', this.activeTiers);
  }

  setupSafeEventListeners() {
    // SAFE event listeners - no function overriding
    const cartEvents = ['cart:updated', 'cart:refresh', 'shopify:cart:update', 'ajaxCart:updated'];
    
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, () => this.safeHandleCartUpdate());
    });

    // Safe click monitoring - no interference with actual functionality
    document.addEventListener('click', (e) => {
      const target = e.target.closest(`
        .cart-remove-button, .cart__remove, .btn--remove, 
        [href*="/cart/change"], [data-href*="/cart/change"], 
        .remove-cart-item, button[data-line], .quantity__button
      `);
      
      if (target) {
        // Only update progress bars, don't interfere with the action
        setTimeout(() => this.updateAllProgressBars(), 200);
        setTimeout(() => this.updateAllProgressBars(), 1000);
      }
    });

    // Safe quantity change monitoring
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name*="updates"], .quantity__input, input[name="quantity"]')) {
        setTimeout(() => this.updateAllProgressBars(), 200);
      }
    });

    // Safe Shopify cart update monitoring
    if (window.Shopify && window.Shopify.onCartUpdate) {
      const originalHandler = window.Shopify.onCartUpdate;
      window.Shopify.onCartUpdate = (cart) => {
        if (originalHandler) originalHandler(cart);
        this.safeHandleCartUpdate();
      };
    }
  }

  safeHandleCartUpdate() {
    if (this.isUpdating) return;
    setTimeout(() => this.updateAllProgressBars(), 100);
    setTimeout(() => this.updateAllProgressBars(), 500);
  }

  startSafeMonitoring() {
    // Safe periodic monitoring - just checks for changes, doesn't interfere
    setInterval(async () => {
      if (!this.isUpdating) {
        await this.checkForCartChanges();
      }
    }, 3000); // Every 3 seconds
  }

async checkForCartChanges() {
  try {
    const cartData = await this.fetchCartData();
    if (!cartData) return;

    const totalChanged = this.lastCartTotal !== cartData.progress_total_price;
    const countChanged = this.lastItemCount !== cartData.item_count;

    if (totalChanged || countChanged) {
      this.lastCartTotal = cartData.progress_total_price;
      this.lastItemCount = cartData.item_count;
      await this.updateAllProgressBars();
    }
  } catch (error) {
    console.warn('Cart monitoring error:', error);
  }
}


async fetchCartData() {
  try {
    const response = await fetch('/cart.json');
    const data = await response.json();
    // Use original_total_price for progress bar calculations
    if (data) {
      data.progress_total_price = data.original_total_price || data.total_price;
      data.free_shipping_progress_total_price = data.progress_total_price;
      data.items.forEach((item, index) => {
        if (item.gift_card == true) {
          data.progress_total_price -= item.final_line_price;
        }
      });
    }
    return data;
  } catch (error) {
    console.warn('Cart data fetch failed:', error);
    return null;
  }
}


 async updateAllProgressBars() {
  if (this.isUpdating) return;
  this.isUpdating = true;

  try {
    const cartData = await this.fetchCartData();
    if (!cartData) return;

    this.lastCartTotal = cartData.progress_total_price;
    this.lastItemCount = cartData.item_count;
    
    const progressData = this.calculateProgressData(cartData.progress_total_price, cartData.free_shipping_progress_total_price);
    
    this.containers.forEach(container => {
      this.updateProgressBarInstance(container, progressData, cartData.progress_total_price, cartData.free_shipping_progress_total_price);
    });

  } catch (error) {
    console.error('Progress bar update failed:', error);
  } finally {
    this.isUpdating = false;
  }
}


  calculateProgressData(cartTotal, cartTotalFree) {
    var old_cartTotal = cartTotal;
    if (this.activeTiers.length === 0) {
      return { percentage: 0, currentTier: 0, nextThreshold: 0, nextReward: '' };
    }

    const tierCount = this.activeTiers.length;
    let breakpoints;
  

    if (tierCount === 1) {
      breakpoints = [100];
    } else if (tierCount === 2) {
      breakpoints = [50, 100];
    } else if (tierCount === 3) {
      breakpoints = [33.33, 66.67, 100];
    }

    let percentage = 0;
    let currentTier = 0;
    let nextThreshold = 0;
    let nextReward = '';

    for (let i = 0; i < this.activeTiers.length; i++) {
      const tier = this.activeTiers[i];

      if (i == 0 || cartTotal == 0) {
        cartTotal = cartTotalFree;
      } else {
        cartTotal = old_cartTotal;
      }


      if (cartTotal >= tier.priceInPaise) {
        currentTier = i + 1;

        console.log(i, this.activeTiers.length - 1);
        
        if (i === this.activeTiers.length - 1) {
          percentage = 100;
          nextThreshold = 0;
          nextReward = '';
        } else {
          const nextTier = this.activeTiers[i + 1];
          const tierProgress = (cartTotal - tier.priceInPaise) / (nextTier.priceInPaise - tier.priceInPaise);
          const tierRange = breakpoints[i + 1] - breakpoints[i];
          
          percentage = breakpoints[i] + (tierProgress * tierRange);
          if (old_cartTotal == 0) {
            percentage = 33.33;
          }
          console.log(percentage, cartTotal, breakpoints[i] , tierProgress, tierRange, nextTier);
          nextThreshold = nextTier.priceInPaise;
          nextReward = nextTier.heading;
        }
      } else {
        if (currentTier === 0) {
          percentage = (cartTotal / tier.priceInPaise) * breakpoints[i];
          nextThreshold = tier.priceInPaise;
          nextReward = tier.heading;
        }
        break;
      }
    }

    return {
      percentage: Math.min(Math.max(percentage, 0), 100),
      currentTier,
      nextThreshold,
      nextReward
    };
  }

  updateProgressBarInstance(container, progressData, cartTotal, cartTotalFree) {
    const { percentage, currentTier, nextThreshold, nextReward } = progressData;

    this.updateProgressBarFill(container, percentage);
    this.updateProgressText(container, percentage, nextThreshold, nextReward, cartTotal);
    this.updateTierAchievements(container, currentTier);
  }

  updateProgressBarFill(container, percentage) {
    const fillBar = container.querySelector('.fill-bar');
    if (fillBar) {
      fillBar.style.width = `${percentage}%`;
    }
  }

  updateProgressText(container, percentage, nextThreshold, nextReward, cartTotal) {
    const textElement = container.querySelector('.progress-bar-text p');
    if (!textElement) return;

    if (percentage >= 100 && nextThreshold === 0) {
      textElement.innerHTML = '<strong>🎉 Congratulations! You\'ve unlocked all rewards!</strong>';
    } else if (nextThreshold > 0) {
      const amountNeeded = this.formatCurrency(nextThreshold - cartTotal);
      
      let rewardText = nextReward;
      if (!rewardText || rewardText.trim() === '') {
        const matchingTier = this.activeTiers.find(tier => tier.priceInPaise === nextThreshold);
        rewardText = matchingTier ? matchingTier.heading : 'your next reward';
      }
      
      textElement.innerHTML = `<strong>${amountNeeded} away from ${rewardText}!</strong>`;
    }
  }

  updateTierAchievements(container, currentTier) {
    container.querySelectorAll('.progressbar-cols').forEach((column, index) => {
      const tierNumber = index + 1;
      column.classList.toggle('achieved', currentTier >= tierNumber);
    });
  }

  formatCurrency(amountInPaise) {
    const rupees = amountInPaise / 100;
    return `₹${rupees.toFixed(2)}`; 
  }
}

// Safe initialization
function safeInitializeProgressBar() {
  if (!window.progressBarManagerInstance && document.querySelector('.progress-bar-section')) {
    window.progressBarManagerInstance = new CartProgressBarManager();
  }
}

// Safe event listeners for initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInitializeProgressBar);
} else {
  safeInitializeProgressBar();
}

window.addEventListener('load', safeInitializeProgressBar);

// Make globally accessible
window.CartProgressBarManager = CartProgressBarManager;
